/**
 * Upload policy — one definition, used by every upload surface in the app.
 *
 * The Lockerroom bench, the Library and the three agent step-1 pickers all
 * validate against this module. That is the point: in 2.0 each surface carried
 * its own accept list and its own size cap, so a file the bench took could be
 * rejected by an agent that was supposed to consume it.
 *
 * Pure. No React, no I/O, no project imports — so the rules are unit-testable
 * in a node environment and cannot drift from what the UI enforces.
 */

/** Extensions the bench accepts, lower-case and dot-prefixed. */
export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.xlsx',
  '.xls',
  '.csv',
  '.pptx',
  '.ppt',
  '.doc',
  '.docx',
] as const;

export type AcceptedExtension = (typeof ACCEPTED_EXTENSIONS)[number];

/** The `accept` attribute for a file input. Derived, never hand-maintained. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',');

/**
 * 50 MB, stated in bytes rather than as `50 * 1024 * 1024` at each call site.
 * IndexedDB will hold considerably more, but a document larger than this is
 * almost always an export mistake rather than a report.
 */
export const MAX_FILE_BYTES = 50 * 1_048_576;

/** Why a file was refused. `null` means it passed. */
export type UploadRejection =
  | { readonly reason: 'empty'; readonly message: string }
  | { readonly reason: 'too-large'; readonly message: string }
  | { readonly reason: 'unsupported-type'; readonly message: string };

/** Just enough of `File` to validate, so tests need no DOM. */
export interface FileLike {
  readonly name: string;
  readonly size: number;
}

/** Lower-case extension including the dot, or '' when the name carries none. */
export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  // A leading dot is a hidden file, not an extension.
  if (dot <= 0) return '';
  return fileName.slice(dot).toLowerCase();
}

/** The file name without its extension — seeds the document title. */
export function stripExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot <= 0 ? fileName : fileName.slice(0, dot);
}

export function isAcceptedExtension(fileName: string): boolean {
  const ext = extensionOf(fileName);
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(ext);
}

/**
 * Human-readable size. Binary units, because that is what the OS file dialog
 * shows the user immediately before they get here.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 0 || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(2)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
}

/**
 * The single gate. Returns `null` when the file is acceptable, otherwise the
 * reason — with a message written for the user, not for a log.
 *
 * Order matters: an empty file is reported as empty even when its extension is
 * also wrong, because "this file is 0 bytes" is the more actionable complaint.
 */
export function validateFile(file: FileLike): UploadRejection | null {
  if (file.size <= 0) {
    return {
      reason: 'empty',
      message: `“${file.name}” is empty (0 bytes). Check the export and try again.`,
    };
  }
  if (file.size > MAX_FILE_BYTES) {
    return {
      reason: 'too-large',
      message: `“${file.name}” is ${formatFileSize(file.size)}. The limit is ${formatFileSize(
        MAX_FILE_BYTES,
      )}.`,
    };
  }
  if (!isAcceptedExtension(file.name)) {
    const ext = extensionOf(file.name);
    return {
      reason: 'unsupported-type',
      message:
        ext === ''
          ? `“${file.name}” has no file extension, so its type cannot be determined.`
          : `${ext} files are not supported. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`,
    };
  }
  return null;
}
